import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import {
	fetchSharedQueriesWithMe,
	shareProspectQuery,
	shareProspectQueryWithOrg,
	unshareProspectQuery,
	removeShareForCurrentUser,
	fetchSharesForQuery,
} from '../../api/resourceSharesApi';

import { prospectQueryKeys } from './keys';

const sharingKeys = {
	all: ['resourceShares'],
	sharedWithMe: () => [...sharingKeys.all, 'sharedWithMe'],
	forQuery: (queryId) => [...sharingKeys.all, 'forQuery', queryId],
};

export function useSharedQueries() {
	return useQuery({
		queryKey: sharingKeys.sharedWithMe(),
		queryFn: fetchSharedQueriesWithMe,
		staleTime: STALE_TIMES.STANDARD,
		retry: 2,
	});
}

export function useSharesForQuery(queryId) {
	return useQuery({
		queryKey: sharingKeys.forQuery(queryId),
		queryFn: () => fetchSharesForQuery(queryId),
		staleTime: STALE_TIMES.STANDARD,
		enabled: Boolean(queryId),
	});
}

export function useShareProspectQuery() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ queryId, sharedWithUserId, shareWithOrg = false }) => {
			if (shareWithOrg) {
				return await shareProspectQueryWithOrg(queryId);
			}
			return await shareProspectQuery(queryId, { sharedWithUserId });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: sharingKeys.all });
			queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});
}

export function useUnshareProspectQuery() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (shareId) => unshareProspectQuery(shareId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: sharingKeys.all });
			queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});
}

export function useRemoveShareForCurrentUser() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (resourceId) => removeShareForCurrentUser(resourceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: sharingKeys.all });
			queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});
}
